import * as React from 'react';

import { usePrevious, useRequest, useUpdateEffect } from 'ahooks';

import { PrimitiveTabs } from 'components';
import { useProductAction, useProductValue } from 'context';
import { getCategory } from 'services';

import { ProductAttributesInfo } from './attributes-info';
import { ProductBaseInfo } from './base-info';
import { ProductComplementsInfo } from './complements-info';
import { ProductFilesInfo } from './files-info';
import { ProductModelsInfo } from './models-info';
import { ProductPackagingInfo } from './packaging-info';
import { ProductSeoInfo } from './seo-info';
import { ProductVariantsInfo } from './variants-info';

const ProductFormTabs = () => {
  const { runAsync } = useRequest(getCategory, {
    manual: true,
  });
  const {
    mainCategory,
    productType,
    variableAttributes = [],
    baseAttributes = [],
    subProducts = [],
    packagingInfo = [],
    step = 1,
  } = useProductValue();
  const previousMainCategory = usePrevious(mainCategory);
  const action = useProductAction();

  React.useEffect(() => {
    const { id: mainCategoryId } = mainCategory ?? {};

    if (mainCategoryId) {
      runAsync({ id: mainCategoryId }).then(({ attributes }) => {
        action({
          type: 'UPDATE_PRODUCT_BASE_ATTRIBUTES',
          payload: {
            baseAttributes: attributes.map(attribute => {
              const { id: attributeId } = attribute;
              const { selectedValues = [] } =
                baseAttributes.find(({ id }) => id === attributeId) ?? {};

              return {
                ...attribute,
                selectedValues,
              };
            }),
          },
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainCategory]);

  useUpdateEffect(() => {
    const isModifiedMainCategory =
      mainCategory?.id !== previousMainCategory?.id;

    action({
      type: 'UPDATE_PRODUCT_VARIABLE_ATTRIBUTES',
      payload: {
        variableAttributes: !isModifiedMainCategory ? variableAttributes : [],
      },
    });
    action({
      type: 'UPDATE_SUB_PRODUCTS',
      payload: {
        subProducts: !(isModifiedMainCategory && productType === 'Variable')
          ? subProducts
          : [],
        selectedSubProduct: undefined,
      },
    });
    action({
      type: 'UPDATE_PRODUCT_PACKAGING_INFO',
      payload: {
        packagingInfo: !isModifiedMainCategory ? packagingInfo : [],
        selectedPackage: undefined,
      },
    });
  }, [mainCategory, previousMainCategory]);

  return (
    <PrimitiveTabs
      items={[
        {
          key: '1',
          label: 'اطلاعات اولیه',
          children: <ProductBaseInfo />,
        },
        {
          key: '2',
          label: 'مشخصات فنی',
          children: <ProductAttributesInfo />,
          disabled: step < 2,
        },
        {
          key: '3',
          label: 'مدیریت مدل‌ها',
          children: <ProductModelsInfo />,
          disabled: step < 3,
        },
        {
          key: '4',
          label: 'مدیریت مستندات',
          children: <ProductFilesInfo />,
          disabled: step < 4,
        },
        {
          key: '5',
          label: 'مدیریت بسته‌بندی‌ها',
          children: <ProductPackagingInfo />,
          disabled: step < 5,
        },
        {
          key: '6',
          label: 'مدیریت متغیرها',
          children: <ProductVariantsInfo />,
          disabled: step < 6,
        },
        {
          key: '7',
          label: 'مدیریت مکمل‌ها',
          children: <ProductComplementsInfo />,
          disabled: step < 7,
        },
        {
          key: '8',
          label: 'تنظیمات سئو',
          children: <ProductSeoInfo />,
          disabled: step < 8,
        },
      ]}
      activeKey={step.toString()}
      onChange={activeKey => {
        action({
          type: 'UPDATE_PRODUCT_STEP',
          payload: {
            step: +activeKey as typeof step,
          },
        });
      }}
    />
  );
};

export { ProductFormTabs };
